import { useEffect } from "react";
import { useAtom } from "jotai";
import { useSearchParams } from "react-router-dom";

import {
  expertPanelVar,
  languageVar,
  multiLingualToastOpenVar,
} from "@/variables/globalVar";
import { cn } from "shared/lib";

import { CaptureEventType, useCaptureEvent } from "@/hooks";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  WorldIcon,
} from "shared/components";

const languages = [
  { value: "zh-Hans", label: "简体中文" },
  { value: "zh-Hant", label: "繁體中文" },
  { value: "vi", label: "Tiếng Việt" },
  { value: "ar", label: "العربية" },
  { value: "it", label: "Italiano" },
  { value: "es", label: "Español" },
  { value: "id", label: "Bahasa Indonesia" },
  { value: "hi", label: "हिंदी" },
  { value: "fr", label: "Français" },
  { value: "ja", label: "日本語" },
  { value: "ru", label: "Русский" },
  { value: "fa", label: "فارسی" },
];

export interface LanguageDropdownProps {
  className?: string;
  disabled?: boolean;
  whiteListOptionValues?: Array<string>;
}

export function LanguageDropdown({
  className,
  disabled,
  whiteListOptionValues,
}: LanguageDropdownProps) {
  const { captureEvent } = useCaptureEvent();

  const [searchParams] = useSearchParams();
  const rtlParam = searchParams.get("rtl");

  const [language, setLanguageVar] = useAtom(languageVar);
  const [, setExpertPanelVar] = useAtom(expertPanelVar);
  const [, setMultiLingualToastOpenVar] = useAtom(multiLingualToastOpenVar);

  useEffect(() => {
    const locale = new Intl.Locale(language || navigator.language);

    let textInfo: { direction: "ltr" | "rtl" } | undefined;
    if ("getTextInfo" in locale && typeof locale.getTextInfo === "function") {
      textInfo = locale.getTextInfo();
    } else if ("textInfo" in locale) {
      textInfo = locale.textInfo as any;
    }

    if (textInfo?.direction === "rtl") {
      document.body.setAttribute("dir", "rtl");
    } else {
      document.body.setAttribute("dir", "ltr");
    }
  }, [language]);

  useEffect(() => {
    // Remove both font classes first
    document.body.classList.remove(
      "font-noto-sans-arabic",
      "font-noto-sans-sc",
    );

    if (language === "ar") {
      document.body.classList.add("!font-noto-sans-arabic");
    } else if (language === "zh-hans") {
      document.body.classList.add("!font-noto-sans-sc");
    }
    // For other languages, no special font class is added (default applies)
  }, [language]);

  useEffect(() => {
    if (rtlParam === "true") {
      document.body.setAttribute("dir", "rtl");
    } else if (rtlParam === "false") {
      document.body.setAttribute("dir", "ltr");
    }
  }, [rtlParam]);

  return (
    <Select
      disabled={disabled}
      onValueChange={(lang) => {
        setLanguageVar(lang);
        setMultiLingualToastOpenVar(lang !== "en");
        // Reset the selected panel when language changes to prevent using non-translatable panels
        setExpertPanelVar(null);
        captureEvent(CaptureEventType.USER_LANGUAGE_CHANGED, { lang });
      }}
      value={language}
    >
      <SelectTrigger
        className={cn(
          "border-interactiveBorder w-auto rounded-sm border",
          className,
        )}
      >
        <WorldIcon className="mr-1 size-4" />
        <SelectValue placeholder="Language" />
      </SelectTrigger>

      <SelectContent className="bg-interfaceWhite max-h-[16rem] overflow-auto">
        <SelectItem
          className="hover:bg-interactiveBgHover"
          title="en"
          value="en"
        >
          English
        </SelectItem>

        {languages.map(({ value, label }) => {
          return (
            <SelectItem
              className="hover:bg-interactiveBgHover"
              hidden={
                Array.isArray(whiteListOptionValues)
                  ? !whiteListOptionValues.includes(value)
                  : false
              }
              key={value}
              title={value}
              value={value}
            >
              {label}
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}
